// The bid home's Jobs section (AB#462, components/JobsSection.tsx): how often it reads the list, and
// a job's kind, subject, status and note in the app's words. The status mapping and the percentage
// are the backend's (backend/domain/jobs.ts): this only says them. A new kind adds a line here, not
// a section. Pure — no React, no fetch.
import type { Key } from '../i18n'
import type { BidJob, BidJobKind, BidJobNote, BidJobStatus } from './api'
import type { Words } from './slide-import'

/** The query key of a bid's jobs (`GET /v1/bids/:bid/jobs`). */
export const bidJobsKey = (bid: string) => ['bid-jobs', bid] as const

/** While a job runs: its percentage moves, so the section follows it. */
const RUNNING_POLL_MS = 3000
/** With nothing running: just enough to see a job launched elsewhere (another tab, an agent, another member). */
const IDLE_POLL_MS = 15_000

export const jobInFlight = (job: Pick<BidJob, 'status'>): boolean => job.status === 'planned' || job.status === 'in_progress'

export function jobsPollMs(jobs: ReadonlyArray<Pick<BidJob, 'status'>>): number {
  return jobs.some(jobInFlight) ? RUNNING_POLL_MS : IDLE_POLL_MS
}

const KIND_WORDS: Record<BidJobKind, Key> = {
  pptx_import: 'jobs_kind_pptx_import',
  indexing: 'jobs_kind_indexing',
}
export const jobKindWords = (kind: BidJobKind): Key => KIND_WORDS[kind]

const STATUS_WORDS: Record<BidJobStatus, Key> = {
  planned: 'jobs_status_planned',
  in_progress: 'jobs_status_in_progress',
  completed: 'jobs_status_completed',
  failed: 'jobs_status_failed',
}

/** The status alone, which is what a change of it announces: a percentage is no change. */
export const jobStatusWords = (status: BidJobStatus): Key => STATUS_WORDS[status]

/** The status as the row says it, with the percentage in the text. */
export function jobStatusText(job: Pick<BidJob, 'status' | 'percent'>): Words {
  return job.status === 'in_progress' && job.percent !== undefined
    ? { key: 'jobs_status_in_progress_at', vars: { percent: job.percent } }
    : { key: STATUS_WORDS[job.status] }
}

const NOTE_WORDS: Record<BidJobNote, Key> = {
  renders_failed: 'jobs_note_renders_failed',
  interrupted: 'jobs_note_interrupted',
}
export const jobNoteWords = (note: BidJobNote): Key => NOTE_WORDS[note]

/** What a job works on: its name as it stands, else its number of files, else (still walking) that it is counting them. */
export function jobSubject(job: Pick<BidJob, 'subject' | 'status'>): string | Words | null {
  const { name, files } = job.subject
  if (name) return name
  if (files !== undefined) return files === 1 ? { key: 'jobs_subject_file' } : { key: 'jobs_subject_files', vars: { n: files } }
  return jobInFlight(job) ? { key: 'jobs_subject_counting' } : null
}
